<template>
  <div class="page-wrap resendOTPCode">
    <v-form ref="form" v-model="valid" @submit.prevent="lookUpMember">
      <!-- Step 1: Membership Number Lookup -->
      <v-card class="tool-card" elevation="2">
        <div class="tool-card__accent"></div>

        <v-card-item class="pt-5">
          <template v-slot:prepend>
            <v-avatar color="secondary" rounded="lg" size="44">
              <v-icon icon="mdi-lock-reset" color="#231F20" size="24"></v-icon>
            </v-avatar>
          </template>
          <v-card-title class="text-h6">Resend Login Code</v-card-title>
          <v-card-subtitle class="tool-card__subtitle">
            Use this if someone calls in saying they need a code to log in.
          </v-card-subtitle>
        </v-card-item>

        <v-divider class="mt-4"></v-divider>

        <v-card-text class="pt-6 pb-2">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="membershipNumber"
                :rules="membershipNumberRules"
                label="Membership Number"
                prepend-inner-icon="mdi-pound"
                inputmode="numeric"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            type="submit"
            :disabled="!membershipNumber || busy"
            :loading="lookingUp"
            color="primary"
            variant="flat"
            size="large"
          >
            Look Up Member
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-alert v-if="error" type="error" variant="tonal"
        title="Please correct the following error(s):" class="text-start mt-6" icon="mdi-alert-circle-outline">
        {{ errorMessage || "Sorry something went wrong Try Again" }}
      </v-alert>

      <v-alert v-if="passCodeStatus" type="success" variant="tonal"
        title="Success" class="text-start mt-6" icon="mdi-check-circle-outline">
        {{ successMessage }}
      </v-alert>

      <!-- Step 2: Member Data + Verification -->
      <v-card v-if="memberData" class="mt-6" elevation="2">
        <v-card-item class="pt-5">
          <v-card-title class="text-subtitle-1 font-weight-bold">Member Information</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pt-5">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="field-label">Name</div>
              <div class="field-value">{{ memberData.account_name || "—" }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="field-label">Membership #</div>
              <div class="field-value">{{ membershipNumber }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="field-label">Email on File</div>
              <div class="field-value">{{ memberData.email || "—" }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="field-label">Phone on File</div>
              <div class="field-value">{{ memberData.mobile_phone || "—" }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-checkbox
            v-model="dataVerified"
            color="primary"
            label="I have verified this member's identity using the information above"
            hide-details
          />
        </v-card-text>
      </v-card>

      <!-- Step 3: Send Code (only after verification) -->
      <div v-if="memberData && dataVerified" class="send-actions mt-6">
        <v-btn
          :variant="selectedMethod === 'email' ? 'flat' : 'outlined'"
          color="primary"
          :loading="sending === 'email'"
          :disabled="busy"
          size="large"
          @click="sendPasscode('email')"
        >
          <v-icon start>mdi-email-outline</v-icon>
          Send Email Passcode
        </v-btn>
        <v-btn
          v-if="smsAvailable"
          :variant="selectedMethod === 'sms' ? 'flat' : 'outlined'"
          color="primary"
          :loading="sending === 'sms'"
          :disabled="busy"
          size="large"
          @click="sendPasscode('sms')"
        >
          <v-icon start>mdi-cellphone-message</v-icon>
          Send Text Message Passcode
        </v-btn>
      </div>
    </v-form>
  </div>
</template>


<script>
import api, { apiErrorMessage } from "../services/api";
import { membershipNumberRules } from "../utils/validators";

export default {
  name: "ResendOTPCode",
  data() {
    return {
      valid: false,
      selectedMethod: null,
      memberData: null,
      membershipNumberRules,
      membershipNumber: null,
      dataVerified: null,
      passCodeStatus: null,
      lookingUp: false,
      sending: null,
      error: false,
      errorMessage: null,
    };
  },
  computed: {
    // Any request in flight — used to disable every button so a rep cannot
    // fire off two one-time passcodes by double-clicking.
    busy() {
      return this.lookingUp || this.sending !== null;
    },
    // Absence of the flag means "not disabled". The previous `=== false` check
    // made the button vanish silently whenever the API omitted the key. A
    // number on file is the real prerequisite for sending a text.
    smsAvailable() {
      return !this.memberData?.sms_login_disabled && !!this.memberData?.mobile_phone;
    },
    successMessage() {
      if (this.selectedMethod === "sms") {
        return "Login code was sent by text message";
      }
      if (this.selectedMethod === "email") {
        return "Login code was sent by email";
      }
      return "Login Code was sent successfully";
    },
  },
  methods: {
    resetMemberData() {
      this.dataVerified = null;
      this.memberData = null;
      this.selectedMethod = null;
    },
    clearState() {
      this.error = false;
      this.errorMessage = null;
      this.passCodeStatus = null;
    },
    async lookUpMember() {
      if (this.busy) return;
      this.clearState();
      this.resetMemberData();
      await this.getMemberData();
    },
    async getMemberData() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;
      this.lookingUp = true;
      try {
        const response = await api.post("/cchelper/resendcode/data", {
          membershipNumber: this.membershipNumber,
        });
        this.memberData = response.data;
      } catch (error) {
        this.error = true;
        this.errorMessage = apiErrorMessage(
          error,
          "Something went wrong trying to get membership data"
        );
      } finally {
        this.lookingUp = false;
      }
    },
    // One entry point for both delivery methods so the guard, the in-flight
    // flag and the error handling cannot drift apart between them.
    async sendPasscode(method) {
      if (this.busy) return;
      this.clearState();
      this.selectedMethod = method;
      this.sending = method;
      try {
        const response =
          method === "sms"
            ? await api.post("/login/otp/sms/send", {
                phoneNumber: this.memberData.mobile_phone,
                email: this.memberData.email,
                recordId: this.memberData.record_id,
              })
            : await api.post("/login/otp/email/send", {
                email: this.memberData.email,
              });
        this.passCodeStatus = response.status;
      } catch (error) {
        this.error = true;
        this.errorMessage = apiErrorMessage(
          error,
          method === "sms"
            ? "Something went wrong trying to send passcode via text"
            : "Something went wrong trying to send passcode via email"
        );
      } finally {
        this.sending = null;
      }
    },
  },
};
</script>

<style scoped>
.field-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.65;
  margin-bottom: 2px;
}

.field-value {
  font-size: 1rem;
  font-weight: 500;
  word-break: break-word;
}

/* Wraps instead of overflowing when both delivery buttons are shown. */
.send-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
</style>
